import { useQuery } from "@apollo/client";
import { useState } from "react";
import {
  GetReviewQuestionReachQuery,
  GetReviewQuestionReachQueryVariables,
  ReviewQuestionAction,
  ReviewQuestionWordingScope,
} from "types/graphql-schema";

import getReviewQuestionReachQuery from "@apps/programs/graphql/get-review-question-reach-query";
import { useProgramsSection } from "@apps/programs/programs-section-context";
import Button from "@components/button/button";
import { buttonTheme } from "@components/button/button-constants";
import Input from "@components/input/input";
import Modal from "@components/modal/modal";
import ModalTitle from "@components/modal/modal-title";
import TextareaWysiwyg from "@components/wysiwyg/textarea-wysiwyg";

import {
  ReviewQuestionRow,
  ReviewQuestionScope,
  describeQuestionReach,
} from "./review-question-rows";

const isTiptapDoc = (value: string | null) => {
  try {
    return !!value && JSON.parse(value)?.type === "doc";
  } catch {
    return false;
  }
};

/**
 * Edit a question's title and description on a published review.
 *
 * Every edit asks where it applies: "Only in this review" stores the wording for this review,
 * "Fix everywhere" edits the question itself, so other reviews and the library change too.
 * The dialog names how far that goes.
 */
const EditQuestionWordingDialog = ({
  question,
  templateFlags,
  review,
  loading,
  onSave,
  onClose,
}: {
  question: ReviewQuestionRow;
  templateFlags: Omit<
    GetReviewQuestionReachQueryVariables,
    "complianceProgramId" | "questionId"
  >;
  review: ReviewQuestionScope;
  loading: boolean;
  onSave: (variables: {
    action: ReviewQuestionAction;
    questionId: number;
    title: string | null;
    description: string | null;
    scope: ReviewQuestionWordingScope;
  }) => void;
  onClose: () => void;
}) => {
  const { noun } = useProgramsSection();
  const nounLower = noun.toLowerCase();
  const [title, setTitle] = useState(question.title);
  // The editor hands back its own JSON string, so the description only counts as changed
  // once the admin types in it.
  const [description, setDescription] = useState<string | null>(null);
  const [scope, setScope] = useState(ReviewQuestionWordingScope.ThisReview);

  const { data: reachData } = useQuery<
    GetReviewQuestionReachQuery,
    GetReviewQuestionReachQueryVariables
  >(getReviewQuestionReachQuery, {
    variables: {
      complianceProgramId: review.complianceProgramId,
      questionId: question.questionId,
      ...templateFlags,
    },
    fetchPolicy: "network-only",
  });
  const program = reachData?.complianceProgram;
  const reach = [
    program?.performanceAssessmentTemplate,
    program?.managerAssessmentTemplate,
    program?.peerAssessmentTemplate,
    program?.engagementSurveyTemplate,
  ].find(Boolean)?.questionReach;

  // Fixing everywhere the wording this review already has is a change: it moves that wording
  // onto the question itself, so every review shows it.
  const fixesOwnWording =
    scope === ReviewQuestionWordingScope.Everywhere && question.changed;
  const newTitle =
    fixesOwnWording || title.trim() !== question.title ? title.trim() : null;
  // A question without a description stores `{}`, which the backend refuses as a description.
  const newDescription =
    description ??
    (fixesOwnWording && isTiptapDoc(question.description)
      ? question.description
      : null);
  const hasChange = newTitle !== null || newDescription !== null;
  const saveTooltip =
    title.trim() === ""
      ? "A question needs a title"
      : !hasChange
        ? "Change the title or the description first"
        : undefined;

  return (
    <Modal onClose={onClose} className="z-tooltip">
      <div
        className="flex flex-col text-sm p-6 gap-4"
        aria-label="Edit question wording dialog"
      >
        <ModalTitle onClose={onClose}>Edit wording</ModalTitle>
        <Input
          label="Title"
          aria-label="Question title input"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
        />
        <div>
          <div className="text-gray-500 text-xs font-medium mb-1">
            Description
          </div>
          <TextareaWysiwyg
            editable
            className="bg-white"
            placeholder="Add a description (optional)"
            value={question.description}
            onChangeValue={setDescription}
          />
        </div>
        <div className="flex flex-col gap-2">
          <div className="font-medium">Where does this change apply?</div>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              name="question-wording-scope"
              checked={scope === ReviewQuestionWordingScope.ThisReview}
              onChange={() => setScope(ReviewQuestionWordingScope.ThisReview)}
            />
            Only in this {nounLower}
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              name="question-wording-scope"
              checked={scope === ReviewQuestionWordingScope.Everywhere}
              onChange={() => setScope(ReviewQuestionWordingScope.Everywhere)}
            />
            Fix everywhere
          </label>
          {scope === ReviewQuestionWordingScope.Everywhere && (
            <div className="ml-6 text-gray-600">
              {reach
                ? describeQuestionReach(reach, nounLower)
                : "Checking where else this question is used..."}
            </div>
          )}
        </div>
        <div className="flex justify-end gap-3">
          <Button theme={buttonTheme.text} onClick={onClose} text="Cancel" />
          <Button
            theme={buttonTheme.primary}
            disabled={loading || !!saveTooltip}
            tooltip={saveTooltip}
            onClick={() =>
              onSave({
                action: ReviewQuestionAction.RewordQuestion,
                questionId: question.questionId,
                title: newTitle,
                description: newDescription,
                scope,
              })
            }
            text="Save"
          />
        </div>
      </div>
    </Modal>
  );
};

export default EditQuestionWordingDialog;
